import type {
  Api,
  BaseQueryFn,
  EndpointDefinitions,
  QueryArgFrom,
  QueryKeys,
  ResultTypeFrom,
} from "@reduxjs/toolkit/query";

/**
 * The entity a "show" query returns. The `id` is required because it is what
 * the default query-arg builder patches by.
 */
type CachedEntity<
  Definitions extends EndpointDefinitions,
  EndpointName extends QueryKeys<Definitions>,
> = ResultTypeFrom<Definitions[EndpointName]> & { id: string };

/**
 * Builds the query arg the cache entry is keyed by.
 */
type QueryArgBuilder<
  Definitions extends EndpointDefinitions,
  EndpointName extends QueryKeys<Definitions>,
> = (
  data: CachedEntity<Definitions, EndpointName>,
) => QueryArgFrom<Definitions[EndpointName]>;

/**
 * The query-arg-builder argument. Optional when the endpoint is keyed by plain
 * `{ id }`, so the default builder applies; required otherwise, because a wrong
 * key does not fail — `updateQueryData` returns early for an uninitialized cache
 * entry, so the optimistic update would silently never happen. The `[ ]` wrap
 * keeps the check non-distributive over unions.
 */
type QueryArgArg<
  Definitions extends EndpointDefinitions,
  EndpointName extends QueryKeys<Definitions>,
> = [{ id: string }] extends [QueryArgFrom<Definitions[EndpointName]>]
  ? [queryArg?: QueryArgBuilder<Definitions, EndpointName>]
  : [queryArg: QueryArgBuilder<Definitions, EndpointName>];

/**
 * Builds an optimistic cache-patch action creator for a "show" query.
 * Returns `(data) => ThunkAction`; dispatch it to overwrite the cache entry for
 * `data` with the full entity.
 *
 * Fully typed against the api's endpoint definitions:
 *  - `endpoint` is constrained to the api's query-endpoint names, so a mutation
 *    name or a typo is a compile error,
 *  - `data` is that endpoint's result type (and must carry an `id`), and
 *  - `queryArg` must return that endpoint's query-arg type.
 *
 * `queryArg` defaults to `{ id: data.id }` and may be omitted only for endpoints
 * actually keyed that way; for any other key it is mandatory. That matters
 * because a wrong key does not fail loudly — `updateQueryData` returns early for
 * an uninitialized cache entry, so the optimistic update would simply never
 * happen.
 *
 * All of `Api`'s parameters (base query, reducer path, tag types) are inferred
 * because the endpoint definitions embed them — pinning them to `string` breaks
 * assignability of a concrete injected api.
 *
 * Call this at module scope, next to the api it patches, and export the result
 * under a name. Called inline in a render it returns a new function every time,
 * which churns the identity of consumers that memoise on it — such as the `set`
 * of `useOptimisticDebouncedMutation`.
 *
 * @example
 * export const updateAhuDataCache = updateCache(
 *   ahuDataApi,
 *   "htzAirHandlingUnitDataShow",
 * );
 *
 * @example
 * // show query keyed by `outboxId`, not by `id`
 * const update = updateCache(
 *   outboxGesecInvoiceApi,
 *   "businessCentralOutboxGesecInvoicesShow",
 *   (invoice) => ({ outboxId: invoice.outboxId }),
 * );
 */
export function updateCache<
  BaseQuery extends BaseQueryFn,
  Definitions extends EndpointDefinitions,
  ReducerPath extends string,
  TagTypes extends string,
  EndpointName extends QueryKeys<Definitions>,
>(
  api: Api<BaseQuery, Definitions, ReducerPath, TagTypes>,
  endpoint: EndpointName,
  ...rest: QueryArgArg<Definitions, EndpointName>
) {
  const queryArg = rest[0] ?? defaultQueryArg;

  // Instantiate `updateQueryData` for this endpoint so we can name its exact
  // arg type. TS cannot reduce `QueryArgFromAnyQueryDefinition` to `QueryArgFrom`
  // while `EndpointName` is generic, so the arg needs this one cast — sound
  // because `EndpointName extends QueryKeys` guarantees a query endpoint.
  const patch = api.util.updateQueryData<EndpointName>;
  type Arg = Parameters<typeof patch>[1];

  return (data: CachedEntity<Definitions, EndpointName>) =>
    patch(endpoint, queryArg(data) as unknown as Arg, () => data);
}

function defaultQueryArg<
  Definitions extends EndpointDefinitions,
  EndpointName extends QueryKeys<Definitions>,
>(data: CachedEntity<Definitions, EndpointName>) {
  return { id: data.id } as unknown as QueryArgFrom<Definitions[EndpointName]>;
}
